import React, { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Collapse,
  styled,
  Card,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { ReactComponent as SmallDhiramIcon } from "../../../assets/svg/SmallDhiramIcon.svg";
import { renderCurrencyIcon } from "../../../utils/CurrencyUtils";
import moment from "moment";
import { statusStyleArray } from "../config";
import { useNavigate } from "react-router-dom";

const StyledCard = styled(Card)(({ theme }) => ({
  margin: "0 !important",
  borderRadius: "0",
  boxShadow: "none",
  borderBottom: "1px solid #D7D8D9",
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const BalanceDetailsMobile = ({ details, fetchData }) => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState({});

  const handleExpandClick = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePayoutClick = (proposalId) => {
    navigate(`/project/details/${proposalId}/payments`);
  };

  const handleCardClick = (proposalId) => {
    handlePayoutClick(proposalId);
  };

  return (
    <Box sx={{ padding: "2vh 0", backgroundColor: "#fff" }}>
      {!details || details.length === 0 ? (
        <Typography
          variant="body1"
          sx={{ textAlign: "center", marginTop: 4, color: "#666" }}
        >
          No project data available
        </Typography>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          {details?.map((project, idx) => (
            <StyledCard
              key={project.id || idx}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#333",
                      marginBottom: "8px",
                      letterSpacing: "0"
                    }}
                  >
                    {project?.name}
                  </Typography>
                </div>

                {project.payouts && project.payouts.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandClick(project.id || idx);
                    }}
                    style={{ marginLeft: "8px" }}
                  >
                    {expandedRows[project.id || idx] ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Total Project Value
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {renderCurrencyIcon('small')}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                      }}
                    >
                      {formattedAmountWithoutAed(project.totalCost)}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Total Paid Amount
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {renderCurrencyIcon('small')}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                      }}
                    >
                      {formattedAmountWithoutAed(project.totalPaidAmount)}
                    </Typography>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Total Due Amount
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {renderCurrencyIcon('small')}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                      }}
                    >
                      {formattedAmountWithoutAed(project.totalDueAmount)}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Total Not Due Amount
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {renderCurrencyIcon('small')}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                      }}
                    >
                      {formattedAmountWithoutAed(project.totalNotDueAmount)}
                    </Typography>
                  </div>
                </div>
              </div>

              {expandedRows[project.id || idx] && project.payouts && (
                <Collapse in={expandedRows[project.id || idx]} timeout="auto">
                  <Box
                    sx={{
                      margin: "16px 0",
                      padding: "16px",
                      backgroundColor: "#F8FAFC",
                      borderRadius: "12px",
                      border: "1px solid #E2E8F0",
                    }}
              onClick={() => handleCardClick(project.proposalId)}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#1E293B",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "4px",
                          height: "16px",
                          backgroundColor: "#4D6BF6",
                          borderRadius: "2px",
                        }}
                      />
                      Payouts
                    </Typography>
                    {project.payouts.map((payout, payoutIdx) => {
                      let metaData = null;
                      let invoiceData = null;

                      try {
                        if (payout?.metadata) {
                          metaData = JSON.parse(payout.metadata);
                        }
                        if (payout?.invoiceUrl) {
                          invoiceData = JSON.parse(payout.invoiceUrl);
                        }
                      } catch (error) {
                        console.warn("Error parsing payout metadata:", error);
                      }

                      return (
                        <div
                          key={payout.groupName || payoutIdx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 0",
                            borderBottom:
                              payoutIdx < project.payouts.length - 1
                                ? "1px solid #E2E8F0"
                                : "none",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#1E293B",
                                fontWeight: "600",
                                marginBottom: "4px",
                              }}
                            >
                              {payout.groupName || `Payout ${payoutIdx + 1}`}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#64748B",
                              }}
                            >
                              {payout.dueDate
                                ? moment(payout.dueDate).format("MMM DD, YYYY")
                                : payout.releaseDate
                                ? moment(payout.releaseDate).format(
                                    "MMM DD, YYYY"
                                  )
                                : "---"}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {renderCurrencyIcon('small')}
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#1E293B",
                                fontWeight: "600",
                              }}
                            >
                              {formattedAmountWithoutAed(payout.amount)}
                            </Typography>
                          </div>
                        </div>
                      );
                    })}
                  </Box>
                </Collapse>
              )}
            </StyledCard>
          ))}
        </div>
      )}
    </Box>
  );
};

export default BalanceDetailsMobile;
